import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useConnectionStore } from '@/stores/connectionStore'
import { useForm, Controller } from 'react-hook-form'
import { XMarkIcon, EyeIcon, EyeSlashIcon, PlusIcon, TrashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { useAgents } from '@/hooks/useAgents'
import { useCreateDataSourceMutation, useUpdateDataSourceMutation } from '@/hooks/useDataSources'
import { PaginatedSelect } from '@/components/common/PaginatedSelect'
import { createDatasources, deleteDatasources, type Agent, type DataSourceDetails } from '@/services/api'
import { notify } from '@/utils/notify'
import { AgentProxyService } from '@/services/agentProxyService'

interface ConnectionModalProps {
  existingConnection?: DataSourceDetails
  onClose: () => void
}

interface MemcachedServer {
  host: string
  port: number
}

interface RedisClusterNode {
  host: string
  port: number
}

interface ConnectionFormData {
  name: string
  type: 'mysql' | 'postgresql' | 'redis' | 'memcached' | 'oracle' | 'mongodb' | 'clickhouse' | 'scylladb' | 'milvus'
  host: string
  port: number
  username: string
  password: string
  database?: string
  ssl?: boolean
  // Required for API
  agentId: string
  isLocked?: boolean
  // Memcached-specific: additional servers for clustering
  memcachedServers?: MemcachedServer[]
}


export function ConnectionModal({ existingConnection, onClose }: ConnectionModalProps) {
  const { addConnection, updateConnection } = useConnectionStore()
  const [showPassword, setShowPassword] = useState(false)

  // Memcached additional servers state
  const [memcachedServers, setMemcachedServers] = useState<MemcachedServer[]>([])
  const [newServerHost, setNewServerHost] = useState('')
  const [newServerPort, setNewServerPort] = useState(11211)

  // Redis Cluster mode state
  const [isClusterMode, setIsClusterMode] = useState(false)
  const [clusterNodes, setClusterNodes] = useState<RedisClusterNode[]>([])
  const [newClusterNodeHost, setNewClusterNodeHost] = useState('')
  const [newClusterNodePort, setNewClusterNodePort] = useState(6379)

  // Track whether form type has synced with existingConnection (to distinguish initial load from user changes)
  const formTypeSyncedRef = useRef(false)
  // Track whether cluster data has been initialized from existingConnection
  const clusterDataInitializedRef = useRef(false)
  // Track previous existingConnection to detect when it changes (use symbol as sentinel for initial state)
  const INITIAL_SENTINEL = useRef(Symbol('initial'))
  const prevExistingConnectionRef = useRef<DataSourceDetails | undefined | symbol>(INITIAL_SENTINEL.current)

  // Test connection state
  const [isTestingConnection, setIsTestingConnection] = useState(false)
  const [testConnectionResult, setTestConnectionResult] = useState<{ success: boolean; message: string; version?: string } | null>(null)
  
  // API mutations for datasource operations
  const createDataSourceMutation = useCreateDataSourceMutation()
  const updateDataSourceMutation = useUpdateDataSourceMutation()
  
  // Pagination state for agents
  const [agentPage, setAgentPage] = useState(1)
  const [hasMoreAgents, setHasMoreAgents] = useState(true)
  const [allAgents, setAllAgents] = useState<Agent[]>([])
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [isLoadingMoreAgents, setIsLoadingMoreAgents] = useState(false)
  
  // Fetch agents for the dropdown
  const { agents, loading: agentsLoading, error: agentsError, fetchAgents, getAgent, pagination } = useAgents()

  // Calculate form default values based on mode
  const defaultValues = useMemo((): ConnectionFormData => {
    return existingConnection ? {
      name: existingConnection.displayName || '',
      type: (
        existingConnection.databaseType === 'MySQL' ? 'mysql' :
        existingConnection.databaseType === 'Redis' ? 'redis' :
        // Redis Cluster is detected via clusterNodes in connectionParams, not databaseType
        existingConnection.databaseType === 'Memcached' ? 'memcached' :
        existingConnection.databaseType === 'OracleDB' ? 'oracle' :
        existingConnection.databaseType === 'MongoDB' ? 'mongodb' :
        existingConnection.databaseType === 'ClickHouse' ? 'clickhouse' :
        existingConnection.databaseType === 'ScyllaDB' ? 'scylladb' :
        existingConnection.databaseType === 'Milvus' ? 'milvus' :
        'postgresql'
      ),
      host: existingConnection.host || 'localhost',
      port: existingConnection.port || (
        existingConnection.databaseType === 'MySQL' ? 3306 :
        existingConnection.databaseType === 'Redis' ? 6379 :
        existingConnection.databaseType === 'Memcached' ? 11211 :
        existingConnection.databaseType === 'OracleDB' ? 1521 :
        existingConnection.databaseType === 'MongoDB' ? 27017 :
        existingConnection.databaseType === 'ClickHouse' ? 8123 :
        existingConnection.databaseType === 'ScyllaDB' ? 9042 :
        existingConnection.databaseType === 'Milvus' ? 19530 :
        5432
      ),
      username: existingConnection.username || '',
      // Never populate password for security
      password: '',
      database: existingConnection.defaultDatabase || '',
      ssl: (existingConnection as unknown as { connectionParams?: { ssl?: boolean } }).connectionParams?.ssl || false,
      agentId: existingConnection.agentId || '',
      // Load lock status from server
      isLocked: existingConnection.isLocked || false,
    } : {
      name: '',
      type: 'mysql',
      host: 'localhost',
      port: 3306,
      username: '',
      password: '',
      database: '',
      ssl: false,
      agentId: '',
      isLocked: false,
    }
  }, [existingConnection])

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<ConnectionFormData>({
    defaultValues
  })

  const watchType = watch('type')
  const watchAgentId = watch('agentId')

  // Check if an agent is selected (required before filling other fields)
  const isAgentSelected = Boolean(watchAgentId)

  // Check if no agents are available
  const noAgentsAvailable = !agentsLoading && !agentsError && allAgents.length === 0

  // Check if the selected agent is online (connected)
  const selectedAgentData = useMemo(() => {
    if (!watchAgentId) return null
    return allAgents.find(a => a.id === watchAgentId) || selectedAgent
  }, [watchAgentId, allAgents, selectedAgent])

  const isSelectedAgentOnline = selectedAgentData?.connection_status === 'connected'

  // Only set default port for NEW connections when type changes
  useEffect(() => {
    // Detect if existingConnection just changed - if so, skip clearing to let form sync first
    const existingConnectionChanged = prevExistingConnectionRef.current !== existingConnection
    if (existingConnectionChanged) {
      prevExistingConnectionRef.current = existingConnection
      // Reset sync tracking since we're switching to a different connection
      formTypeSyncedRef.current = false
    }

    if (!existingConnection) {
      if (watchType === 'mysql') {
        setValue('port', 3306)
      } else if (watchType === 'redis') {
        setValue('port', 6379)
      } else if (watchType === 'memcached') {
        setValue('port', 11211)
      } else if (watchType === 'oracle') {
        setValue('port', 1521)
      } else if (watchType === 'mongodb') {
        setValue('port', 27017)
      } else if (watchType === 'clickhouse') {
        // HTTP interface (most common)
        setValue('port', 8123)
        // ClickHouse default user
        setValue('username', 'default')
      } else if (watchType === 'scylladb') {
        // ScyllaDB CQL native transport port
        setValue('port', 9042)
        // ScyllaDB default user
        setValue('username', 'cassandra')
      } else if (watchType === 'milvus') {
        setValue('port', 19530)
        // Milvus default user
        setValue('username', 'root')
      } else {
        setValue('port', 5432)
      }
    }

    // Determine expected type from existingConnection
    const expectedType = existingConnection ? (
      existingConnection.databaseType === 'MySQL' ? 'mysql' :
      existingConnection.databaseType === 'Redis' ? 'redis' :
      existingConnection.databaseType === 'Memcached' ? 'memcached' :
      existingConnection.databaseType === 'OracleDB' ? 'oracle' :
      existingConnection.databaseType === 'MongoDB' ? 'mongodb' :
      existingConnection.databaseType === 'ClickHouse' ? 'clickhouse' :
      existingConnection.databaseType === 'ScyllaDB' ? 'scylladb' :
      existingConnection.databaseType === 'Milvus' ? 'milvus' :
      'postgresql'
    ) : null

    // Track when form type syncs with existingConnection type
    if (existingConnection && watchType === expectedType) {
      formTypeSyncedRef.current = true
    }

    // Skip clearing logic when existingConnection just changed - form hasn't synced yet
    if (existingConnectionChanged) {
      return
    }

    // Clear memcached servers when switching away from memcached.
    // Only clear for new connections OR when form has synced and user actively changed type.
    if (watchType !== 'memcached') {
      // Determine if we should clear: new connection, not editing memcached, or user changed type
      const shouldClear = !existingConnection ||
        expectedType !== 'memcached' ||
        formTypeSyncedRef.current
      if (shouldClear) {
        setMemcachedServers([])
        setNewServerHost('')
        setNewServerPort(11211)
      }
    }
    // Clear cluster mode and nodes when switching away from redis.
    // Only clear for new connections OR when the user actively changed type after form initialization.
    if (watchType !== 'redis') {
      const isEditingRedisConnection = existingConnection?.databaseType === 'Redis'
      // Don't clear if we're editing a Redis connection but form hasn't been synced yet.
      // This prevents clearing during the initial form load before the cluster loading effect runs.
      // Only clear when: new connection, not editing Redis, or user changed type AFTER form was synced.
      const shouldClear = !existingConnection ||
        !isEditingRedisConnection ||
        formTypeSyncedRef.current
      if (shouldClear) {
        setIsClusterMode(false)
        setClusterNodes([])
        setNewClusterNodeHost('')
        setNewClusterNodePort(6379)
      }
    }
  }, [watchType, setValue, existingConnection])

  // Track if we've fetched agents to prevent duplicates
  const hasFetchedAgentsRef = useRef(false)
  
  // Load initial agents only when modal opens and agents haven't been loaded
  useEffect(() => {
    // Only fetch if we haven't fetched yet and there are no agents
    if (!hasFetchedAgentsRef.current && agents.length === 0 && !agentsLoading) {
      hasFetchedAgentsRef.current = true
      const loadInitialAgents = async () => {
        // Explicitly pass empty filters to ensure we get all agents, not filtered ones
        await fetchAgents(1, 20, {})
      }
      void loadInitialAgents()
    }
  }, [fetchAgents, agents.length, agentsLoading])

  // Update all agents when new page is loaded
  useEffect(() => {
    if (agents.length > 0) {
      setAllAgents(prev => {
        // Create a map to ensure uniqueness by ID
        const agentMap = new Map(prev.map(agent => [agent.id, agent]))
        for (const agent of agents) agentMap.set(agent.id, agent)
        return Array.from(agentMap.values())
      })

      // Update hasMoreAgents based on pagination info
      if (pagination) {
        setHasMoreAgents(pagination.has_next ?? false)
      }
    }
  }, [agents, pagination])

  // Load specific agent data when editing
  useEffect(() => {
    const loadSelectedAgent = async () => {
      if (existingConnection?.agentId && !selectedAgent) {
        // First check if agent is already in our loaded list
        const foundAgent = allAgents.find(a => a.id === existingConnection.agentId)
        if (foundAgent) {
          setSelectedAgent(foundAgent)
        } else {
          // If not found, fetch the specific agent
          const agent = await getAgent(existingConnection.agentId)
          if (agent) {
            setSelectedAgent(agent)
            // Add to allAgents if not already there
            setAllAgents(prev => {
              const exists = prev.some(a => a.id === agent.id)
              return exists ? prev : [...prev, agent]
            })
          }
        }
      }
    }
    void loadSelectedAgent()
  }, [existingConnection, allAgents, getAgent, selectedAgent])

  // Reset form sync tracking when existingConnection changes
  useEffect(() => {
    formTypeSyncedRef.current = false
    clusterDataInitializedRef.current = false
  }, [existingConnection])

  // Reset form when defaultValues change (e.g., when switching between connections)
  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  // Load memcached servers from existing connection
  useEffect(() => {
    if (existingConnection?.databaseType === 'Memcached' && existingConnection.connectionParams) {
      const servers = existingConnection.connectionParams.servers as MemcachedServer[] | undefined
      if (Array.isArray(servers)) {
        setMemcachedServers(servers)
      }
    } else {
      setMemcachedServers([])
    }
  }, [existingConnection])

  // Load Redis Cluster mode and nodes from existing connection
  useEffect(() => {
    // Detect cluster mode from presence of clusterNodes in Redis connection
    const rawClusterNodes = existingConnection?.connectionParams?.clusterNodes as
      RedisClusterNode[] | undefined

    // Check if this is a Redis Cluster connection with valid cluster nodes.
    // The clusterNodes should be an array with at least one node (indicating cluster mode).
    const isValidClusterNodes = Array.isArray(rawClusterNodes) && rawClusterNodes.length > 0
    const isCluster = existingConnection?.databaseType === 'Redis' && isValidClusterNodes

    if (isCluster) {
      setIsClusterMode(true)
      // Type-safe mapping of cluster nodes
      const nodes = rawClusterNodes.map(node => ({
        host: String(node.host || ''),
        port: Number(node.port) || 6379
      }))
      setClusterNodes(nodes)
      // Mark that cluster data has been initialized from existingConnection
      clusterDataInitializedRef.current = true
    } else if (existingConnection?.databaseType === 'Redis' && rawClusterNodes !== undefined) {
      // Redis connection with clusterNodes field present but empty or invalid
      // This indicates cluster mode was enabled but no nodes were added, or data is corrupted
      // Treat as cluster mode with empty nodes so user can fix it
      setIsClusterMode(Array.isArray(rawClusterNodes))
      setClusterNodes([])
      clusterDataInitializedRef.current = true
    } else if (!existingConnection) {
      // New connection - no initialization needed, just reset state
      setIsClusterMode(false)
      setClusterNodes([])
    }
    // Note: For non-cluster Redis connections, clusterDataInitializedRef is not set so users can enable cluster mode manually
  }, [existingConnection])

  // Memcached server management functions
  const addMemcachedServer = useCallback(() => {
    if (!newServerHost.trim()) return
    const port = newServerPort || 11211
    if (port < 1 || port > 65535) return

    // Check for duplicates
    const exists = memcachedServers.some(
      s => s.host === newServerHost.trim() && s.port === port
    )
    if (exists) return

    setMemcachedServers(prev => [...prev, { host: newServerHost.trim(), port }])
    setNewServerHost('')
    setNewServerPort(11211)
  }, [newServerHost, newServerPort, memcachedServers])

  const removeMemcachedServer = useCallback((index: number) => {
    setMemcachedServers(prev => prev.filter((_, i) => i !== index))
  }, [])

  // Redis Cluster node management functions
  const addClusterNode = useCallback(() => {
    if (!newClusterNodeHost.trim()) return
    const port = newClusterNodePort || 6379
    if (port < 1 || port > 65535) return

    // Check for duplicates
    const exists = clusterNodes.some(
      n => n.host === newClusterNodeHost.trim() && n.port === port
    )
    if (exists) return

    setClusterNodes(prev => [...prev, { host: newClusterNodeHost.trim(), port }])
    setNewClusterNodeHost('')
    setNewClusterNodePort(6379)
  }, [newClusterNodeHost, newClusterNodePort, clusterNodes])

  const removeClusterNode = useCallback((index: number) => {
    setClusterNodes(prev => prev.filter((_, i) => i !== index))
  }, [])

  // Callback functions for PaginatedSelect to avoid creating new functions in JSX
  const handleLoadMoreAgents = useCallback(() => {
    if (isLoadingMoreAgents || !hasMoreAgents) return

    const loadMore = async () => {
      setIsLoadingMoreAgents(true)
      try {
        // Explicitly pass empty filters to ensure we get all agents, not filtered ones
        await fetchAgents(agentPage + 1, 20, {})
        setAgentPage(prev => prev + 1)
      } catch (error: unknown) {
        console.error('Failed to load more agents:', error)
      } finally {
        setIsLoadingMoreAgents(false)
      }
    }
    void loadMore()
  }, [isLoadingMoreAgents, hasMoreAgents, fetchAgents, agentPage])

  const getAgentValue = useCallback((agent: Agent) => agent.id || '', [])

  const getAgentLabel = useCallback((agent: Agent) => {
    const statusIcon =
      agent.connection_status === 'connected' ? '🟢' :
      agent.connection_status === 'error' ? '🔴' :
      '🟡'
    const statusText =
      agent.connection_status === 'connected' ? 'Online' :
      agent.connection_status === 'error' ? 'Error' :
      'Offline'
    return `${agent.name} ${statusIcon} (${statusText})`
  }, [])

  // Memoized filtered agents to avoid creating new objects in JSX
  const filteredAgents = useMemo(() => 
    allAgents.filter(agent => agent.is_active !== false), 
    [allAgents]
  )

  // Memoized validation rules
  const agentValidationRules = useMemo(() => ({ 
    required: 'Agent selection is required' 
  }), [])

  // Render function for Controller to avoid creating new functions in JSX
  const renderAgentSelect = useCallback(({ field }: { field: { value: string; onChange: (value: string) => void } }) => (
    <PaginatedSelect
      value={field.value}
      onChange={field.onChange}
      placeholder="Select an agent (required)"
      isLoading={agentsLoading}
      isFetchingNextPage={isLoadingMoreAgents}
      hasNextPage={hasMoreAgents}
      onFetchNextPage={handleLoadMoreAgents}
      options={filteredAgents}
      getOptionValue={getAgentValue}
      getOptionLabel={getAgentLabel}
      disabled={agentsLoading && allAgents.length === 0}
      className="w-full"
      dropdownDirection="auto"
      maxDropdownHeight="10rem"
    />
  ), [agentsLoading, isLoadingMoreAgents, hasMoreAgents, handleLoadMoreAgents, filteredAgents, getAgentValue, getAgentLabel, allAgents.length])

  // Test connection handler
  const handleTestConnection = useCallback(async () => {
    // Get current form values
    const formValues = watch()

    // Validate required fields
    if (!formValues.agentId) {
      notify.error('Please select an agent first')
      return
    }
    if (!formValues.host) {
      notify.error('Please enter a host')
      return
    }
    if (!formValues.port) {
      notify.error('Please enter a port')
      return
    }

    // Check if the selected agent is online
    const agentData = allAgents.find(a => a.id === formValues.agentId) || selectedAgent
    if (agentData && agentData.connection_status !== 'connected') {
      notify.error('The selected agent is not online. Please select an online agent or make the agent online first.')
      return
    }

    // Note: For Redis Cluster, the primary node (host:port) is sufficient.
    // Additional cluster nodes in clusterNodes array are optional for high availability.

    setIsTestingConnection(true)
    setTestConnectionResult(null)

    let tempDataSourceId: string | null = null
    // Track if we're using an existing datasource (edit mode with empty password)
    let useExistingDatasource = false

    try {
      // Build connection params
      const buildConnectionParams = (): Record<string, unknown> | null => {
        const params: Record<string, unknown> = {}

        if (formValues.ssl) {
          params.ssl = true
        }

        if (formValues.type === 'memcached' && memcachedServers.length > 0) {
          params.servers = memcachedServers
        }

        if (formValues.type === 'redis' && isClusterMode) {
          params.clusterNodes = clusterNodes
        }

        return Object.keys(params).length > 0 ? params : null
      }

      // Get username based on database type
      const getUsername = (): string => {
        if (formValues.type === 'memcached') {
          return 'default'
        }
        if (formValues.type === 'redis' && (!formValues.username || !formValues.username.trim())) {
          return 'default'
        }
        return formValues.username || ''
      }

      // Get password based on database type
      const getPassword = (): string => {
        if (formValues.type === 'memcached') {
          return ''
        }
        return formValues.password || ''
      }

      let datasourceIdToTest: string

      // If editing an existing connection and password is empty, use the existing datasource
      // This allows testing without re-entering the password (server will use stored password)
      const isEditMode = Boolean(existingConnection?.id)
      const passwordIsEmpty = !formValues.password || formValues.password.trim() === ''

      // Check if connection settings have changed from the original
      const hasConnectionSettingsChanged = isEditMode && existingConnection && (
        formValues.host !== existingConnection.host ||
        formValues.port !== existingConnection.port ||
        formValues.username !== existingConnection.username ||
        formValues.database !== (existingConnection.defaultDatabase || '') ||
        formValues.ssl !== ((existingConnection as unknown as { connectionParams?: { ssl?: boolean } }).connectionParams?.ssl || false) ||
        formValues.agentId !== existingConnection.agentId
      )

      if (isEditMode && passwordIsEmpty && existingConnection?.id && !hasConnectionSettingsChanged) {
        // Use the existing datasource directly for testing (no settings changed)
        datasourceIdToTest = existingConnection.id
        useExistingDatasource = true
      } else if (isEditMode && passwordIsEmpty && hasConnectionSettingsChanged) {
        // Settings changed but no password provided - cannot test new settings
        setTestConnectionResult({
          success: false,
          message: 'Please enter the password to test the updated connection settings'
        })
        setIsTestingConnection(false)
        return
      } else {
        // Create temporary datasource for testing (new connection or password changed)
        const createPayload: {
          displayName: string
          databaseType: 'MySQL' | 'PostgreSQL' | 'Redis' | 'Memcached' | 'OracleDB' | 'MongoDB' | 'ClickHouse' | 'ScyllaDB' | 'Milvus'
          host: string
          port: number
          username: string
          password?: string
          defaultDatabase: string | null
          connectionParams: Record<string, unknown> | null
          agentId: string
          isLocked: boolean
        } = {
          displayName: `test-connection-${Date.now()}`,
          databaseType: (
            formValues.type === 'mysql' ? 'MySQL' as const :
            formValues.type === 'redis' ? 'Redis' as const :
            formValues.type === 'memcached' ? 'Memcached' as const :
            formValues.type === 'oracle' ? 'OracleDB' as const :
            formValues.type === 'mongodb' ? 'MongoDB' as const :
            formValues.type === 'clickhouse' ? 'ClickHouse' as const :
            formValues.type === 'scylladb' ? 'ScyllaDB' as const :
            formValues.type === 'milvus' ? 'Milvus' as const :
            'PostgreSQL' as const
          ),
          host: formValues.host,
          port: formValues.port,
          username: getUsername(),
          defaultDatabase: isClusterMode ? null : (formValues.database || null),
          connectionParams: buildConnectionParams(),
          agentId: formValues.agentId,
          isLocked: false
        }

        // Only include password for non-memcached connections
        if (formValues.type !== 'memcached') {
          createPayload.password = getPassword()
        }

        // Create temporary datasource
        const { data: tempDataSource } = await createDatasources({
          body: createPayload as Parameters<typeof createDatasources>[0]['body'],
          throwOnError: true
        })

        if (!tempDataSource.id) {
          setTestConnectionResult({
            success: false,
            message: 'Failed to create temporary data source for testing'
          })
          return
        }

        tempDataSourceId = tempDataSource.id
        datasourceIdToTest = tempDataSource.id
      }

      // Test the connection
      const testResult = await AgentProxyService.testConnection(datasourceIdToTest, formValues.agentId, true)

      // Parse the result
      const isSuccessful = testResult && typeof testResult === 'object' && 'connection_successful' in testResult
        ? Boolean((testResult as { connection_successful: boolean }).connection_successful)
        : false

      const version = testResult && typeof testResult === 'object' && 'version' in testResult
        ? String((testResult as { version: string }).version)
        : undefined

      const databaseType = testResult && typeof testResult === 'object' && 'database_type' in testResult
        ? String((testResult as { database_type: string }).database_type)
        : undefined

      if (isSuccessful) {
        const versionInfo = version ? ` (${databaseType || formValues.type}: ${version})` : ''
        setTestConnectionResult({
          success: true,
          message: `Connection successful${versionInfo}`,
          version
        })
      } else {
        const errorMessage = testResult && typeof testResult === 'object' && 'message' in testResult
          ? String((testResult as { message: string }).message)
          : 'Connection test failed'
        setTestConnectionResult({
          success: false,
          message: errorMessage
        })
      }
    } catch (error) {
      console.error('Test connection error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Connection test failed'
      setTestConnectionResult({
        success: false,
        message: errorMessage
      })
    } finally {
      // Clean up temporary datasource (only if we created one, not when using existing)
      if (tempDataSourceId && !useExistingDatasource) {
        try {
          await deleteDatasources({
            path: { datasource_id: tempDataSourceId },
            query: { test_cleanup: true },
            throwOnError: true
          })
        } catch (cleanupError) {
          console.warn('Failed to clean up temporary data source:', cleanupError)
        }
      }
      setIsTestingConnection(false)
    }
  }, [watch, allAgents, selectedAgent, isClusterMode, clusterNodes, memcachedServers, existingConnection])

  const onSubmit = async (data: ConnectionFormData) => {
    try {
      // Validate that an agent is selected
      if (!data.agentId) {
        notify.error('Please select an agent. All datasources must be associated with an agent.')
        return
      }

      // Validate that the selected agent is online
      const agentData = allAgents.find(a => a.id === data.agentId) || selectedAgent
      if (agentData && agentData.connection_status !== 'connected') {
        notify.error('The selected agent is not online. Please select an online agent or make the agent online first.')
        return
      }

      // Note: Redis Cluster validation - the primary host:port serves as the seed node.
      // Additional cluster nodes in clusterNodes array are optional for high availability.

      // Build connection params based on database type
      const buildConnectionParams = () => {
        const params: Record<string, unknown> = {}

        // Add SSL if enabled
        if (data.ssl) {
          params.ssl = true
        }

        // Add memcached servers if any
        if (data.type === 'memcached' && memcachedServers.length > 0) {
          params.servers = memcachedServers
        }

        // Add Redis Cluster nodes when in cluster mode
        if (data.type === 'redis' && isClusterMode) {
          // Always include clusterNodes array to indicate cluster mode
          params.clusterNodes = clusterNodes
        }

        return Object.keys(params).length > 0 ? params : null
      }

      // For memcached, username and password are optional (use defaults)
      const getUsername = () => {
        if (data.type === 'memcached') {
          // Memcached doesn't use authentication by default
          return 'default'
        }
        if (data.type === 'redis' && (!data.username || !data.username.trim())) {
          return 'default'
        }
        return data.username
      }

      const getPassword = () => {
        if (data.type === 'memcached') {
          // Memcached doesn't use authentication by default
          return ''
        }
        // For Redis/Redis Cluster, return empty string if no password provided (Redis can work without authentication)
        return data.password || ''
      }

      if (existingConnection && existingConnection.id) {
        // Update existing datasource via API
        const updateData: {
          displayName: string
          databaseType: 'MySQL' | 'PostgreSQL' | 'Redis' | 'Memcached' | 'OracleDB' | 'MongoDB' | 'ClickHouse' | 'ScyllaDB' | 'Milvus'
          host: string
          port: number
          username: string
          password?: string
          defaultDatabase: string | null
          connectionParams: Record<string, unknown> | null
          agentId: string
          isLocked: boolean
        } = {
          displayName: data.name,
          databaseType: (
            data.type === 'mysql' ? 'MySQL' as const :
            data.type === 'redis' ? 'Redis' as const :
            data.type === 'memcached' ? 'Memcached' as const :
            data.type === 'oracle' ? 'OracleDB' as const :
            data.type === 'mongodb' ? 'MongoDB' as const :
            data.type === 'clickhouse' ? 'ClickHouse' as const :
            data.type === 'scylladb' ? 'ScyllaDB' as const :
            data.type === 'milvus' ? 'Milvus' as const :
            'PostgreSQL' as const
          ),
          host: data.host,
          port: data.port,
          username: getUsername(),
          // Cluster mode has no database selection
          defaultDatabase: isClusterMode ? null : (data.database || null),
          connectionParams: buildConnectionParams(),
          agentId: data.agentId,
          isLocked: data.isLocked || false
        }

        // Only include password for non-memcached connections (memcached doesn't use authentication)
        // For Redis, password is optional - only include if provided
        // For other databases, only include if updating the password
        if (data.type !== 'memcached') {
          const password = getPassword()
          if (password && password.trim()) {
            updateData.password = password
          }
        }

        await updateDataSourceMutation.mutateAsync({
          id: existingConnection.id,
          data: updateData
        })

        // Also update local store for UI consistency
        updateConnection(existingConnection.id, data)
        notify.success('Connection updated successfully')
      } else {
        // Create new datasource via API
        // For memcached, don't include password field at all (server rejects empty passwords)
        const createPayload: {
          displayName: string
          databaseType: 'MySQL' | 'PostgreSQL' | 'Redis' | 'Memcached' | 'OracleDB' | 'MongoDB' | 'ClickHouse' | 'ScyllaDB' | 'Milvus'
          host: string
          port: number
          username: string
          password?: string
          defaultDatabase: string | null
          connectionParams: Record<string, unknown> | null
          agentId: string
          isLocked: boolean
        } = {
          displayName: data.name,
          databaseType: (
            data.type === 'mysql' ? 'MySQL' as const :
            data.type === 'redis' ? 'Redis' as const :
            data.type === 'memcached' ? 'Memcached' as const :
            data.type === 'oracle' ? 'OracleDB' as const :
            data.type === 'mongodb' ? 'MongoDB' as const :
            data.type === 'clickhouse' ? 'ClickHouse' as const :
            data.type === 'scylladb' ? 'ScyllaDB' as const :
            data.type === 'milvus' ? 'Milvus' as const :
            'PostgreSQL' as const
          ),
          host: data.host,
          port: data.port,
          username: getUsername(),
          // Cluster mode has no database selection
          defaultDatabase: isClusterMode ? null : (data.database || null),
          connectionParams: buildConnectionParams(),
          agentId: data.agentId,
          isLocked: data.isLocked || false
        }

        // Only include password for non-memcached connections
        if (data.type !== 'memcached') {
          // Always include password field - send empty string for Redis without auth
          // Backend should handle empty string as "no authentication"
          createPayload.password = getPassword()
        }

        // Type assertion needed because API spec requires password but memcached doesn't use it
        const newDataSource = await createDataSourceMutation.mutateAsync(
          createPayload as Parameters<typeof createDataSourceMutation.mutateAsync>[0]
        )

        // Add to local store for UI consistency (convert API response to local format)
        const localConnection = {
          ...data,
          id: newDataSource.id || crypto.randomUUID(),
          createdAt: newDataSource.createdAt || new Date().toISOString(),
          updatedAt: newDataSource.updatedAt || new Date().toISOString(),
        }
        addConnection(localConnection)
        notify.success('Connection created successfully')
      }

      onClose()
    } catch (error) {
      console.error('Failed to save connection:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to save connection'
      notify.error(errorMessage)
    }
  }


  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {existingConnection ? 'Edit Database Connection' : 'New Database Connection'}
              </h3>
              <button type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
              {/* Agent Selection - Required First Step */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Agent <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="agentId"
                  control={control}
                  rules={agentValidationRules}
                  render={renderAgentSelect}
                />
                {errors.agentId && (
                  <p className="mt-1 text-sm text-red-600">{errors.agentId.message}</p>
                )}
                {agentsError && (
                  <p className="mt-1 text-sm text-red-600">Failed to load agents</p>
                )}
                {noAgentsAvailable && (
                  <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
                    <p className="text-sm text-amber-800 dark:text-amber-200 font-medium mb-1">
                      No agents available
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                      You need to create an agent first before creating a new connection.
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Go to the Agents page to create a new agent, then return here to create a connection.
                    </p>
                  </div>
                )}
                {!noAgentsAvailable && !isAgentSelected && (
                  <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
                    Please select an agent first to enable the rest of the form
                  </p>
                )}
                {isAgentSelected && !isSelectedAgentOnline && (
                  <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <p className="text-sm text-red-800 dark:text-red-200 font-medium mb-1">
                      Agent is not online
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      The selected agent is currently offline. Please make the agent online first before creating or updating this connection.
                    </p>
                  </div>
                )}
                {isAgentSelected && isSelectedAgentOnline && (
                  <p className="mt-1 text-xs text-gray-500">
                    All datasources must be associated with an agent to handle database operations
                  </p>
                )}
              </div>

              {/* Rest of form fields - disabled until agent is selected AND online */}
              <fieldset disabled={!isAgentSelected || noAgentsAvailable || !isSelectedAgentOnline} className={!isAgentSelected || noAgentsAvailable || !isSelectedAgentOnline ? 'opacity-50' : ''}>
              <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Connection Name
                </label>
                <input
                  {...register('name', { required: 'Connection name is required' })}
                  maxLength={255}
                  className="input w-full"
                  placeholder="My Database"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Database Type
                </label>
                <select
                  {...register('type', { required: 'Database type is required' })}
                  className="input w-full"
                >
                  <option value="mysql">MySQL</option>
                  <option value="postgresql">PostgreSQL</option>
                  <option value="oracle">Oracle</option>
                  <option value="mongodb">MongoDB</option>
                  <option value="clickhouse">ClickHouse</option>
                  <option value="scylladb">ScyllaDB</option>
                  <option value="milvus">Milvus</option>
                  <option value="redis">Redis</option>
                  <option value="memcached">Memcached</option>
                </select>
              </div>

              {/* Redis Cluster Mode Toggle */}
              {watchType === 'redis' && (
                <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                  <input
                    type="checkbox"
                    id="clusterMode"
                    checked={isClusterMode}
                    onChange={(e) => { setIsClusterMode(e.target.checked) }}
                    className="h-4 w-4 text-primary-600 focus:ring-0 border-gray-300 rounded"
                  />
                  <label htmlFor="clusterMode" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                    Cluster Mode
                  </label>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                    Enable for Redis Cluster deployments with sharding
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Host
                  </label>
                  <input
                    {...register('host', { required: 'Host is required' })}
                    maxLength={255}
                    className="input w-full"
                    placeholder="localhost"
                  />
                  {errors.host && (
                    <p className="mt-1 text-sm text-red-600">{errors.host.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Port
                  </label>
                  <input
                    {...register('port', { 
                      required: 'Port is required',
                      valueAsNumber: true,
                      min: { value: 1, message: 'Port must be greater than 0' },
                      max: { value: 65535, message: 'Port must be less than 65536' }
                    })}
                    type="number"
                    className="input w-full"
                  />
                  {errors.port && (
                    <p className="mt-1 text-sm text-red-600">{errors.port.message}</p>
                  )}
                </div>
              </div>

              {/* Username field - hidden for memcached */}
              {watchType !== 'memcached' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Username {watchType === 'redis' && <span className="text-gray-500 text-xs">(Optional, defaults to "default")</span>}
                  </label>
                  <input
                    {...register('username', { required: watchType === 'redis' ? false : 'Username is required' })}
                    maxLength={255}
                    className="input w-full"
                    placeholder={watchType === 'redis' ? 'default' : 'username'}
                  />
                  {errors.username && (
                    <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
                  )}
                </div>
              )}

              {/* Password field - hidden for memcached */}
              {watchType !== 'memcached' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password {watchType === 'redis' && <span className="text-gray-500 text-xs">(Optional for Redis without auth)</span>}
                  </label>
                  <div className="relative">
                    <input
                      {...register('password', {
                        required: existingConnection ? false : (watchType === 'redis' ? false : 'Password is required')
                      })}
                      maxLength={255}
                      type={showPassword ? 'text' : 'password'}
                      className="input w-full pr-10"
                      placeholder={existingConnection ? "Enter password (leave blank to keep current)" : (watchType === 'redis' ? "password (optional)" : "password")}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => { setShowPassword(!showPassword) }}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                  )}
                </div>
              )}

              {watchType !== 'memcached' && !(watchType === 'redis' && isClusterMode) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {watchType === 'redis' ? 'Database Index (Optional)' : watchType === 'oracle' ? 'Service Name' : watchType === 'mongodb' ? 'Auth Database (Optional)' : 'Database (Optional)'}
                  </label>
                  <input
                    {...register('database', { required: watchType === 'oracle' ? 'Service Name is required for Oracle' : false })}
                    maxLength={255}
                    className="input w-full"
                    placeholder={watchType === 'redis' ? '0' : watchType === 'oracle' ? 'XE' : watchType === 'mongodb' ? 'admin' : 'database_name'}
                  />
                  {watchType === 'redis' && (
                    <p className="mt-1 text-xs text-gray-500">Redis database index (0-15 by default)</p>
                  )}
                  {watchType === 'oracle' && (
                    <p className="mt-1 text-xs text-gray-500">Oracle service name (e.g., XE, ORCL, or your PDB name)</p>
                  )}
                </div>
              )}
              {watchType === 'redis' && isClusterMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Additional Cluster Nodes (Optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Add more cluster seed nodes for high availability. The primary node is defined above.
                    Adding multiple nodes ensures the client can discover the cluster even if some nodes are down.
                  </p>

                  {/* Existing cluster nodes list */}
                  {clusterNodes.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {clusterNodes.map((node, index) => (
                        <div
                          key={`${node.host}-${node.port}`}
                          className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md"
                        >
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {node.host}:{node.port}
                          </span>
                          <button
                            type="button"
                            onClick={() => { removeClusterNode(index) }}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Remove node"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add new cluster node form */}
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={newClusterNodeHost}
                        onChange={(e) => { setNewClusterNodeHost(e.target.value) }}
                        placeholder="Host (e.g., redis-node-2)"
                        className="input w-full text-sm"
                      />
                    </div>
                    <div className="w-24">
                      <input
                        type="number"
                        value={newClusterNodePort}
                        onChange={(e) => { setNewClusterNodePort(Number(e.target.value) || 6379) }}
                        placeholder="Port"
                        min={1}
                        max={65535}
                        className="input w-full text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={addClusterNode}
                      disabled={!newClusterNodeHost.trim()}
                      className="btn btn-secondary px-3"
                      title="Add cluster node"
                    >
                      <PlusIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
              {/* Memcached additional servers */}
              {watchType === 'memcached' && (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500">
                    Memcached does not require authentication. The primary server is defined above.
                  </p>

                  <div className="border-t pt-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Additional Servers (Optional)
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Add more memcached servers for distributed caching. Client-side consistent hashing will distribute keys across all servers.
                    </p>

                    {/* Existing servers list */}
                    {memcachedServers.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {memcachedServers.map((server, index) => (
                          <div
                            key={`${server.host}-${server.port}`}
                            className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md"
                          >
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {server.host}:{server.port}
                            </span>
                            <button
                              type="button"
                              onClick={() => { removeMemcachedServer(index) }}
                              className="text-red-500 hover:text-red-700 p-1"
                              title="Remove server"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add new server form */}
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={newServerHost}
                          onChange={(e) => { setNewServerHost(e.target.value) }}
                          className="input w-full text-sm"
                          placeholder="Host (e.g., 192.168.1.2)"
                          maxLength={255}
                        />
                      </div>
                      <div className="w-24">
                        <input
                          type="number"
                          value={newServerPort}
                          onChange={(e) => { setNewServerPort(parseInt(e.target.value, 10) || 11211) }}
                          className="input w-full text-sm"
                          placeholder="Port"
                          min={1}
                          max={65535}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={addMemcachedServer}
                        disabled={!newServerHost.trim()}
                        className="btn-secondary btn-sm flex items-center gap-1"
                        title="Add server"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center">
                <input
                  {...register('ssl')}
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-0 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Use SSL
                </label>
              </div>

              <div className="border-t pt-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    {...register('isLocked')}
                    type="checkbox"
                    className="mt-1 h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                  />
                  <div className="flex-1">
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Lock Connection (Production Safety)
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Require verification code before connecting. Use this for production databases to prevent accidental connections.
                    </span>
                  </div>
                </label>
              </div>
              </div>
              </fieldset>
            </form>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6">
            {/* Test connection result feedback */}
            {testConnectionResult && (
              <div className={`mb-3 p-3 rounded-md flex items-start gap-2 ${
                testConnectionResult.success
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              }`}>
                {testConnectionResult.success ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                )}
                <span className={`text-sm ${
                  testConnectionResult.success
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-red-800 dark:text-red-200'
                }`}>
                  {testConnectionResult.message}
                </span>
              </div>
            )}

            <div className="sm:flex sm:flex-row-reverse">
              <button type="button"
                onClick={() => void handleSubmit(onSubmit)()}
                disabled={createDataSourceMutation.isPending || updateDataSourceMutation.isPending || noAgentsAvailable || !isAgentSelected || !isSelectedAgentOnline || isTestingConnection}
                className="btn-primary btn-md w-full sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createDataSourceMutation.isPending || updateDataSourceMutation.isPending
                  ? (existingConnection ? 'Updating...' : 'Creating...')
                  : (existingConnection ? 'Save Changes' : 'Create Connection')
                }
              </button>
              <button
                type="button"
                onClick={() => void handleTestConnection()}
                disabled={isTestingConnection || noAgentsAvailable || !isAgentSelected || !isSelectedAgentOnline || createDataSourceMutation.isPending || updateDataSourceMutation.isPending}
                data-testid="test-connection-button"
                className="btn-secondary btn-md mt-3 w-full sm:mt-0 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTestingConnection ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                    Testing...
                  </span>
                ) : (
                  'Test Connection'
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={createDataSourceMutation.isPending || updateDataSourceMutation.isPending || isTestingConnection}
                className="btn-ghost btn-md mt-3 w-full sm:mt-0 sm:ml-3 sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}